<template>
  <div class="clinic-dashboard content-wrapper">

    <!-- ===== Header ===== -->
    <div class="d-flex justify-content-between align-items-start mb-2 flex-wrap" style="gap: 12px">
      <div>
        <div class="font-weight-bold">{{ headerTitle }}</div>
        <small class="text-muted">
          {{ branchCount }} สาขา
          <template v-if="c">
            · ข้อมูล <b class="text-dark">{{ rangeLabel }}</b>
            <span v-if="comparisonLabel">
              · เทียบกับ <span class="text-primary-dark">{{ comparisonLabel }}</span>
            </span>
          </template>
        </small>
      </div>

      <!-- ===== Range picker + comparison toggle ===== -->
      <div class="d-flex align-items-end flex-wrap" style="gap: 10px">
        <period-selector :loading="loading" @change="onPeriodChange" />

        <!-- หน้านี้เปิดให้ RDM ด้วยแล้ว ปุ่มจึงต้อง gate เอง — ไม่พึ่งสิทธิ์ของหน้า -->
        <b-button
          v-if="can(PERMISSIONS.DASHBOARD_TARGET_MANAGE)"
          variant="primary"
          class="cd-target-btn"
          @click="$refs.targetModal.open()"
        >
          <i class="fas fa-bullseye mr-1"></i>ตั้งเป้าหมาย
        </b-button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading && !c" class="text-center p-5">
      <b-spinner />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="text-center p-5">
      <p class="text-danger">โหลดข้อมูลไม่สำเร็จ</p>
      <b-button size="sm" variant="outline-primary" @click="load">ลองใหม่</b-button>
    </div>

    <template v-else-if="c">

      <div v-if="!c.summary" class="text-center p-5 text-muted">
        {{ emptyStateText }}
      </div>

      <template v-else>

      <!-- ===== Alerts ===== -->
      <div
        v-if="c.alerts && c.alerts.length"
        class="d-flex align-items-center mb-3"
        style="gap: 8px; overflow-x: auto; flex-wrap: wrap"
      >
        <span class="small font-weight-semibold text-muted flex-shrink-0">ต้องดูวันนี้:</span>
        <span
          v-for="(alert, i) in c.alerts"
          :key="i"
          class="alert-chip"
          :class="alertChipClass(alert)"
          @click="openDrawer(alert.branchUrl, branchNameByUrl[alert.branchUrl])"
        >
          {{ alertIcon(alert) }} {{ alertLabel(alert) }}
        </span>
      </div>

      <!-- ===== KPI band — featured revenue hero + 4 tiles ===== -->
      <div v-if="c.summary" class="cd-kpi-grid mb-3">

        <!-- รายได้รวม (featured hero) -->
        <b-card class="kpi-card cd-kpi-hero h-100" body-class="d-flex flex-column justify-content-center p-3">
          <div class="label-feat small font-weight-semibold text-uppercase">เงินเข้าจริง · {{ scopeLabel }}</div>
          <div class="d-flex align-items-baseline mt-1" style="gap: 6px">
            <span class="hero-num-lg">{{ formatNumber(c.summary.revenue.value) }}</span>
            <span class="unit-baht">฿</span>
          </div>
          <div v-if="!hasDepositActivity" class="label-feat small mt-2">เงินเก็บจริง (cash collected)</div>

          <!-- ที่มาของเงินเข้าจริง — สองบรรทัดนี้บวกกันได้ยอดใหญ่ด้านบนพอดี
               (มัดจำเก่าที่ถูกเอามาตัดค่ารักษา ดูได้ที่บรรทัดมัดจำในตารางสาขา) -->
          <div v-else class="hero-breakdown mt-3">
            <div>
              <span>ค่ารักษาที่เก็บเงินได้</span>
              <b>{{ formatNumber(c.summary.revenue.treatmentCash) }}</b>
            </div>
            <div>
              <span>รับเงินมัดจำ</span>
              <b>+{{ formatNumber(c.summary.revenue.depositReceived) }}</b>
            </div>
          </div>
          <div class="badges mt-3">
            <span
              v-for="(b, i) in revenueBadges"
              :key="i"
              class="hero-badge"
              :class="{ active: b.active }"
            >
              <span class="hero-badge-v" :class="b.pct >= 0 ? 'up' : 'down'">
                {{ b.pct >= 0 ? '▲ +' : '▼ ' }}{{ Math.abs(b.pct).toFixed(1) }}%
              </span>
              <span class="hero-badge-tag">{{ b.tag }}</span>
            </span>
          </div>
          <target-progress
            class="mt-3"
            variant="full"
            dark
            :revenue-target="c.summary.revenueTarget"
            :actual="c.summary.revenue.value"
            :note="revenueTargetNote"
            :start-dt="period && period.startDt"
            :end-dt="period && period.endDt"
          />
        </b-card>

        <!-- ฿/เคส -->
        <b-card class="kpi-card h-100" body-class="p-3 d-flex flex-column">
          <div class="label-muted small font-weight-semibold text-uppercase">฿ ต่อใบเสร็จ</div>
          <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
            <span class="kpi-num font-weight-bold">{{ formatNumber(c.summary.perCase.value) }}</span>
            <span class="small text-muted">฿</span>
          </div>
          <div class="small text-muted mt-1">รายได้ ÷ ใบเสร็จ</div>
          <trend-badge class="mt-2" :pct="perCasePct" :label="comparisonTag" />
        </b-card>

        <!-- ผู้ป่วย -->
        <b-card class="kpi-card h-100" body-class="p-3 d-flex flex-column">
          <div class="label-muted small font-weight-semibold text-uppercase">ผู้ป่วย</div>
          <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
            <span class="kpi-num font-weight-bold">{{ formatNumber(c.summary.patients.total) }}</span>
            <span class="small text-muted">คน</span>
          </div>
          <div class="small text-muted mt-1">
            <span class="text-green font-weight-bold">ผู้ป่วยใหม่ {{ formatNumber(c.summary.patients.new) }} คน</span>
          </div>
          <trend-badge class="mt-2" :pct="c.summary.patientsPrevPct" :label="prevPeriodTag" />
        </b-card>

        <!-- No-show -->
        <b-card class="kpi-card h-100" body-class="p-3 d-flex flex-column">
          <div class="label-muted small font-weight-semibold text-uppercase">No-show</div>
          <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
            <span class="kpi-num font-weight-bold">{{ noShowPctText }}</span>
            <span class="small text-muted">%</span>
          </div>
          <div class="small text-muted mt-1">{{ formatNumber(c.summary.noShow.count) }} / {{ formatNumber(c.summary.noShow.total) }} นัด</div>
        </b-card>

        <!-- ใช้ยูนิตเฉลี่ย (clinic-wide average of branch utilisation) -->
        <b-card class="kpi-card h-100" body-class="p-3 d-flex flex-column">
          <div class="label-muted small font-weight-semibold text-uppercase">ใช้ยูนิตเฉลี่ย</div>
          <div class="d-flex align-items-baseline mt-2" style="gap: 5px">
            <span class="kpi-num font-weight-bold">{{ clinicUtilPct != null ? clinicUtilPct : '–' }}</span>
            <span class="small text-muted">%</span>
          </div>
          <div class="small text-muted mt-1">เฉลี่ย{{ scopeLabel }}</div>
        </b-card>

      </div>

      <!-- ===== Trend + Case Mix ===== -->
      <b-row class="mb-3" style="min-height: 280px">

        <!-- Revenue trend -->
        <b-col md="7" class="mb-2 mb-md-0">
          <b-card class="h-100 d-flex flex-column" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap: 10px">
              <div class="font-weight-bold small">แนวโน้มเงินเข้า · รวม{{ scopeLabel }}</div>
              <div v-if="hasDepositActivity" class="d-flex align-items-center small label-muted" style="gap: 10px">
                <span class="d-flex align-items-center" style="gap: 4px">
                  <span class="legend-dot" style="background: #2196F3"></span>ค่ารักษา
                </span>
                <span class="d-flex align-items-center" style="gap: 4px">
                  <span class="legend-dot" style="background: #26C6DA"></span>รับมัดจำ
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center label-muted small" style="gap: 14px">
              <span>{{ trendSubLabel }}</span>
              <span
                v-if="dailyTarget != null && !isHourlyTrend"
                class="d-inline-flex align-items-center"
                style="gap: 5px; white-space: nowrap"
              >
                <span class="cd-target-dash"></span>
                เป้าเฉลี่ย/{{ isHourlyTrend ? 'ชั่วโมง' : isMonthlyTrend ? 'เดือน' : 'วัน' }} {{ formatShort(dailyTarget) }} ฿<template v-if="revenueTargetNote"> · {{ revenueTargetNote }}</template>
              </span>
            </div>
            <!-- :styles sets the height on vue-chartjs's own wrapper div; without it
                 the canvas has no fixed parent height (maintainAspectRatio:false) and
                 collapses, so nothing renders. -->
            <div class="mt-2" v-if="trendChartData">
              <bar-chart
                :chart-data="trendChartData"
                :options="trendChartOptions"
                :styles="{ position: 'relative', height: '240px' }"
              />
            </div>
            <div v-else class="text-center text-muted small py-4">ไม่มีข้อมูลแนวโน้ม</div>
          </b-card>
        </b-col>

        <!-- Case-mix dual donut -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="h-100 d-flex flex-column" body-class="d-flex flex-column p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div>
                <div class="font-weight-bold small">สัดส่วนหัตถการ · ตามหมวด</div>
                <div class="label-muted small">{{ (period && period.prevPeriodLabel) ? ('เทียบ ' + period.prevPeriodLabel) : 'เทียบช่วงก่อน' }}</div>
              </div>
              <!-- Mode toggle -->
              <div class="cd-seg cd-seg--ghost">
                <button
                  type="button"
                  class="cd-seg-btn"
                  :class="{ on: caseMixMode === 'pct' }"
                  @click="caseMixMode = 'pct'"
                >รายได้ %</button>
                <button
                  type="button"
                  class="cd-seg-btn"
                  :class="{ on: caseMixMode === 'count' }"
                  @click="caseMixMode = 'count'"
                >จำนวน</button>
              </div>
            </div>

            <template v-if="caseMixItems && caseMixItems.length">
              <div class="d-flex flex-wrap" style="gap: 12px">
                <!-- Current period donut -->
                <div class="cd-casemix-col flex-fill" style="min-width: 0">
                  <div class="label-muted small text-center mb-1" style="font-size: 11px">
                    {{ rangeLabel || 'ช่วงนี้' }}
                  </div>
                  <div style="position: relative; height: 130px; width: 130px; margin: 0 auto">
                    <pie-chart :chart-data="caseMixChartData" :options="caseMixOptions" />
                  </div>
                  <div class="mt-2">
                    <div
                      v-for="(item, i) in caseMixItems"
                      :key="i"
                      class="d-flex justify-content-between align-items-center py-1"
                    >
                      <span class="d-flex align-items-center small" style="gap: 5px; min-width: 0">
                        <span class="legend-dot" :style="{ background: item.color }"></span>
                        <span class="text-truncate">{{ item.category }}</span>
                      </span>
                      <span class="small font-weight-bold flex-shrink-0">
                        {{ caseMixMode === 'count' ? item.count : item.pct + '%' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Previous period donut (always shown for comparison) -->
                <div class="cd-casemix-col flex-fill" style="min-width: 0">
                  <div class="label-muted small text-center mb-1" style="font-size: 11px">
                    {{ (period && period.prevPeriodLabel) || 'ช่วงก่อน' }}
                  </div>
                  <template v-if="prevCaseMixItems && prevCaseMixItems.length">
                    <div style="position: relative; height: 130px; width: 130px; margin: 0 auto">
                      <pie-chart :chart-data="caseMixChartDataPrev" :options="caseMixOptions" />
                    </div>
                    <div class="mt-2">
                      <div
                        v-for="(item, i) in prevCaseMixItems"
                        :key="i"
                        class="d-flex justify-content-between align-items-center py-1"
                      >
                        <span class="d-flex align-items-center small" style="gap: 5px; min-width: 0">
                          <span class="legend-dot" :style="{ background: item.color }"></span>
                          <span class="text-truncate">{{ item.category }}</span>
                        </span>
                        <span class="small font-weight-bold flex-shrink-0">
                          {{ caseMixMode === 'count' ? item.count : item.pct + '%' }}
                        </span>
                      </div>
                    </div>
                  </template>
                  <div v-else class="cd-casemix-empty">
                    <div class="cd-casemix-empty-ring"></div>
                    <div class="label-muted small mt-2">ไม่มีข้อมูลช่วงก่อน</div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูล case mix</div>
          </b-card>
        </b-col>

      </b-row>

      <!-- ===== Branches comparison table ===== -->
      <b-card no-body class="mb-3" v-if="sortedBranches.length">
        <div class="d-flex justify-content-between align-items-center p-3 pb-2">
          <div class="font-weight-bold small">เปรียบเทียบรายสาขา</div>
          <span class="small text-muted">เรียงตามรายได้ในช่วง · คลิกเพื่อดูรายละเอียด</span>
        </div>
        <div class="table-responsive">
          <table class="cd-branch-table">
            <thead>
              <tr>
                <th class="text-left">สาขา</th>
                <th>รายได้</th>
                <th>฿/เคส</th>
                <th>ผู้ป่วย (ใหม่)</th>
                <th>No-show</th>
                <th>ใช้ยูนิต</th>
                <th class="text-left">หมอทำเงินสูงสุด</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="branch in sortedBranches"
                :key="branch.branchUrl"
                class="cd-branch-row"
                @click="openDrawer(branch.branchUrl, branch.name)"
              >
                <td class="text-left">
                  <div class="bname">{{ branch.name }}</div>
                  <div class="bsub">/{{ branch.branchUrl }}</div>
                </td>
                <td>
                  <span class="money">฿{{ formatNumber(branch.revenue.value) }}</span>
                  <span
                    v-if="branchRevenuePct(branch) != null"
                    class="branch-delta"
                    :class="branchRevenuePct(branch) >= 0 ? 'text-green' : 'text-red'"
                  >
                    {{ branchRevenuePct(branch) >= 0 ? '▲+' : '▼' }}{{ Math.abs(branchRevenuePct(branch)).toFixed(1) }}%
                  </span>
                  <target-progress
                    variant="badge"
                    :revenue-target="branch.revenueTarget"
                    :actual="branch.revenue.value"
                    :start-dt="period && period.startDt"
                    :end-dt="period && period.endDt"
                  />
                  <!-- แยกให้เห็นว่ายอดของสาขานี้มีมัดจำปนอยู่เท่าไหร่ -->
                  <div v-if="branchHasDeposit(branch)" class="bdeposit">
                    รับมัดจำ {{ formatNumber(branch.revenue.depositReceived) }}
                    <template v-if="branch.revenue.depositUsed">
                      · ใช้มัดจำ {{ formatNumber(branch.revenue.depositUsed) }}
                    </template>
                  </div>
                </td>
                <td class="money">฿{{ formatNumber(branch.perCase) }}</td>
                <td>
                  {{ formatNumber(branch.patients) }}
                  <span class="bsub">({{ formatNumber(branch.newPatients) }})</span>
                </td>
                <td>
                  <span class="pill" :class="noShowPillClass(branch.noShowPct)">
                    {{ (branch.noShowPct || 0).toFixed(1) }}%
                  </span>
                  <span v-if="branch.noShowTotal != null" class="bsub d-block mt-1">
                    {{ formatNumber(branch.noShowCount) }}/{{ formatNumber(branch.noShowTotal) }} นัด
                  </span>
                </td>
                <td>
                  <span v-if="branch.utilPct != null" class="pill" :class="utilPillClass(branch.utilPct)">
                    {{ branch.utilPct }}%
                  </span>
                  <span v-else class="text-muted">–</span>
                </td>
                <td class="text-left">
                  <span v-if="branch.topDoctor" class="bname">{{ stripTitle(branch.topDoctor.name) }}</span>
                  <span v-else class="text-muted">–</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>

      <!-- ===== Doctor revenue + DF ===== -->
      <b-card no-body class="mb-3" v-if="sortedDoctors.length">
        <div class="d-flex justify-content-between align-items-center p-3 pb-2">
          <div>
            <div class="font-weight-bold small">รายได้ต่อทันตแพทย์ · {{ scopeLabel }}</div>
            <!-- ขอบเขตบอกไว้แล้วในหัวการ์ดด้านบน ตรงนี้จึงไม่ผูกกับ scopeLabel —
                 "สัดส่วนของทุกสาขา" อ่านได้ว่าแยกเป็นรายสาขา ทั้งที่ตารางนี้เป็นยอดรวม -->
            <div class="label-muted small">เรียงตามรายได้ · % = สัดส่วนของยอดรวม</div>
          </div>
          <div class="d-flex align-items-center" style="gap: 12px">
            <span class="d-flex align-items-center small label-muted" style="gap: 4px">
              <span class="legend-dot" style="background: linear-gradient(90deg, #2196f3, #5b8dff)"></span>รายได้
            </span>
            <span class="d-flex align-items-center small label-muted" style="gap: 4px">
              <span class="legend-dot" style="background: linear-gradient(90deg, #26c6da, #5be0d0)"></span>DF
            </span>
            <span class="label-muted small">{{ sortedDoctors.length }} ท่าน</span>
          </div>
        </div>
        <div class="px-3 pb-3 d-flex flex-column" style="gap: 10px">
          <div v-for="(doc, i) in sortedDoctors" :key="doc.doctorId || i">
            <div class="d-flex justify-content-between align-items-baseline mb-1">
              <span class="small font-weight-semibold text-truncate" style="max-width: 200px">{{ stripTitle(doc.name) }}</span>
              <div class="d-flex align-items-baseline" style="gap: 8px">
                <span class="small text-muted">{{ formatNumber(doc.cases) }} เคส</span>
                <span class="small font-weight-bold">{{ formatNumber(doc.revenue) }} ฿</span>
                <span class="small text-muted">DF {{ formatNumber(doc.dfShare) }} ฿</span>
                <span class="small font-weight-bold text-primary-dark" style="min-width: 34px; text-align: right">
                  {{ doctorShare(doc.revenue) }}%
                </span>
              </div>
            </div>
            <div class="cd-doctor-bars">
              <div class="cd-doctor-bar-track">
                <div class="cd-doctor-bar-fill" :style="{ width: doctorBarWidth(doc.revenue) + '%' }"></div>
              </div>
              <div class="cd-doctor-bar-track">
                <div class="cd-doctor-bar-fill cd-doctor-bar-fill--df" :style="{ width: doctorBarWidth(doc.dfShare) + '%' }"></div>
              </div>
            </div>
          </div>
        </div>
      </b-card>

      <!-- ===== Top treatments + Referral sources ===== -->
      <b-row class="mb-3">

        <!-- Top treatments -->
        <b-col md="7" class="mb-2 mb-md-0">
          <b-card class="h-100" body-class="p-3">
            <div class="font-weight-bold small mb-2">
              รายการรักษาทำเงินสูงสุด
              <span class="font-weight-normal text-muted">· ตามรายได้</span>
            </div>
            <div
              v-if="c.topTreatments && c.topTreatments.length"
              class="d-flex flex-column"
              style="gap: 11px"
            >
              <div
                v-for="(t, i) in c.topTreatments"
                :key="t.treatmentId || i"
                class="treat-row"
              >
                <div class="treat-rank">{{ i + 1 }}</div>
                <div style="min-width: 0">
                  <div class="d-flex justify-content-between align-items-baseline small mb-1" style="gap: 10px">
                    <span class="text-truncate">{{ t.name }}</span>
                    <span class="font-weight-bold flex-shrink-0">
                      ฿{{ formatNumber(t.revenue) }}
                      <span class="text-muted font-weight-normal" style="font-size: 11.5px">{{ formatNumber(t.count) }} เคส</span>
                    </span>
                  </div>
                  <div class="treat-track">
                    <div class="treat-fill" :style="{ width: treatBarWidth(t.revenue) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลรายการรักษา</div>
          </b-card>
        </b-col>

        <!-- Referral sources -->
        <b-col md="5" class="mb-2 mb-md-0">
          <b-card class="h-100" body-class="p-3">
            <div class="font-weight-bold small mb-2">
              แหล่งที่มาผู้ป่วยใหม่
              <span class="font-weight-normal text-muted">· ในช่วงที่เลือก</span>
            </div>
            <div
              v-if="c.referralSources && c.referralSources.length"
              class="d-flex flex-column"
              style="gap: 11px"
            >
              <div
                v-for="(src, i) in c.referralSources"
                :key="i"
                class="d-flex align-items-center small"
                style="gap: 10px"
              >
                <span class="referral-name text-truncate">{{ src.source }}</span>
                <div class="referral-bar-track flex-fill">
                  <div
                    class="referral-bar-fill"
                    :style="{ width: referralBarWidth(src.count) + '%' }"
                  ></div>
                </div>
                <span class="font-weight-bold flex-shrink-0" style="width: 36px; text-align: right">{{ src.count }}</span>
              </div>
            </div>
            <div v-else class="text-muted small text-center py-4">ไม่มีข้อมูลแหล่งที่มา</div>
          </b-card>
        </b-col>

      </b-row>

      </template>

    </template>

    <!-- Branch Drawer -->
    <branch-drawer
      :visible="drawerOpen"
      :branch-url="activeBranchUrl"
      :branch-name="activeBranchName"
      :start-dt="period && period.startDt"
      :end-dt="period && period.endDt"
      @change="drawerOpen = $event"
      @open-full="openFull"
    />

    <!-- C_LEVEL target-setting modal, opened from the header button -->
    <target-setting-modal ref="targetModal" :clinic-url="clinicUrl" />

  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";
import { PERMISSIONS } from "@/constants/roles";
import { formatNumber } from "@/utils/utils";
import { formatRange, isFullMonthRange } from "@/utils/dashboardPeriod";
import BarChart from "@/components/chart/BarChart.vue";
import PieChart from "@/components/chart/PieChart.vue";
import TrendBadge from "@/components/dashboard/TrendBadge.vue";
import BranchDrawer from "@/components/dashboard/BranchDrawer.vue";
import TargetProgress from "@/components/dashboard/TargetProgress.vue";
import TargetSettingModal from "@/components/dashboard/TargetSettingModal.vue";
import PeriodSelector from "@/components/dashboard/PeriodSelector.vue";
import kpiTargetProgress from "@/utils/kpiTargetProgress";

const CASE_MIX_COLORS = [
  "#2196F3", "#26C6DA", "#8B5CF6", "#F59E0B", "#EF4444", "#94A3B8",
  "#22C55E", "#F97316", "#EC4899", "#64748B",
];

export default {
  name: "ClinicDashboard",

  components: { BarChart, PieChart, TrendBadge, BranchDrawer, TargetProgress, TargetSettingModal, PeriodSelector },

  data() {
    return {
      c: null,
      error: null,
      loading: false,

      // Period data from PeriodSelector
      period: null,

      // Case mix mode toggle
      caseMixMode: "pct",

      // Drawer state
      drawerOpen: false,
      activeBranchUrl: null,
      activeBranchName: "",

      PERMISSIONS,
    };
  },

  computed: {
    ...mapGetters({
      can: "moduleUser/can",
      isClinicWideDashboardViewer: "moduleUser/isClinicWideDashboardViewer",
    }),

    clinicUrl() {
      return this.$route.params.clinicUrl;
    },

    branchCount() {
      return this.c && this.c.branches ? this.c.branches.length : 0;
    },

    // "ทุกสาขา" / "สาขาที่ดูแล" — ใช้แทนคำที่เคยฝังตายทุกจุดบนหน้านี้ ผู้ใช้ที่ถูกจำกัด
    // ขอบเขตต้องไม่เห็นคำว่า "ทุกสาขา" ทับตัวเลขที่มาจากบางสาขา
    scopeLabel() {
      return this.c && this.c.scoped ? "สาขาที่ดูแล" : "ทุกสาขา";
    },

    headerTitle() {
      // ระหว่าง c ยังไม่มา ใช้ permission context แทน กันไม่ให้ RDM เห็นคำว่า
      // "ภาพรวมคลินิก" แวบหนึ่งก่อนสลับเป็น "สาขาที่ดูแล"
      const wide = this.c ? !this.c.scoped : this.isClinicWideDashboardViewer;
      return wide ? "ภาพรวมคลินิก · ทุกสาขา" : "สาขาที่ดูแล";
    },

    // ข้อความกำกับใต้แถบเป้ารวม แสดงเฉพาะตอนที่สาขาในขอบเขตตั้งเป้าไม่ครบ
    // ตั้งครบแล้วไม่ต้องแสดงให้รก
    revenueTargetNote() {
      const t = this.c && this.c.summary && this.c.summary.revenueTarget;
      if (!t || t.branchesWithTarget == null) return "";
      if (t.branchesWithTarget >= t.branchesInScope) return "";
      return `จาก ${t.branchesWithTarget}/${t.branchesInScope} สาขา`;
    },

    // ข้อความตอนไม่มีสาขาให้แสดง — early return ของ BE เกิดได้สองกรณี
    // ผู้ใช้ที่ถูกจำกัดขอบเขตแต่ยังไม่ได้รับมอบหมายสาขา กับคลินิกที่ยังไม่มีสาขาเลย
    emptyStateText() {
      return this.c && this.c.scoped
        ? "ยังไม่ได้รับมอบหมายสาขา — ติดต่อผู้ดูแลระบบเพื่อกำหนดขอบเขตสาขา"
        : "คลินิกนี้ยังไม่มีสาขา";
    },

    // branchUrl -> branch name, for alert chips (alerts carry only branchUrl)
    branchNameByUrl() {
      const map = {};
      if (this.c && this.c.branches) {
        for (const b of this.c.branches) map[b.branchUrl] = b.name;
      }
      return map;
    },

    // ---- Range / comparison labels ----
    rangeLabel() {
      const r = this.c && this.c.range;
      const start = r && r.startDt ? r.startDt : (this.period && this.period.startDt);
      const end = r && r.endDt ? r.endDt : (this.period && this.period.endDt);
      return formatRange(start, end, this.period && this.period.viewType);
    },
    prevPeriodTag() {
      return this.period && this.period.prevPeriodLabel ? "vs " + this.period.prevPeriodLabel : "";
    },
    prevYearTag() {
      return this.period && this.period.prevYearLabel ? "vs " + this.period.prevYearLabel : "";
    },
    comparisonLabel() {
      if (!this.c || !this.c.comparison) return "";
      const comparison = this.period && this.period.comparison;
      const cmp = this.c.comparison[comparison];
      if (!cmp) return "";
      if (comparison === "prevYear") {
        return formatRange(cmp.startDt, cmp.endDt) + " (ปีก่อน)";
      }
      const start = moment.utc(cmp.startDt).local();
      const end = cmp.endDt ? moment.utc(cmp.endDt).local() : null;
      // เดือนเต็มเท่านั้นจึงพูดว่า "เดือนก่อน" — งวดปัจจุบันเทียบกับช่วงบางส่วน (1–9 มิ.ย.)
      if (isFullMonthRange(start, end)) {
        return "เดือนก่อน (" + start.locale("th").format("MMM") + " " + (start.year() + 543) + ")";
      }
      return formatRange(cmp.startDt, cmp.endDt);
    },
    comparisonTag() {
      const comparison = this.period && this.period.comparison;
      return comparison === "prevYear" ? this.prevYearTag : this.prevPeriodTag;
    },

    // ---- Active comparison pct for each KPI ----
    perCasePct() {
      if (!this.c || !this.c.summary) return null;
      return this.c.summary.perCase[((this.period && this.period.comparison) || "prevPeriod") + "Pct"];
    },
    noShowPctText() {
      if (!this.c || !this.c.summary || this.c.summary.noShow.pct == null) return "–";
      return this.c.summary.noShow.pct.toFixed(1);
    },

    // มัดจำเป็นฟีเจอร์ที่หลายคลินิกไม่ได้ใช้ — ซ่อนทุกช่องที่เกี่ยวข้องถ้าช่วงนี้ไม่มีความเคลื่อนไหว
    hasDepositActivity() {
      const r = this.c && this.c.summary && this.c.summary.revenue;
      return !!r && !!(r.depositReceived || r.depositUsed);
    },

    // ---- Hero revenue comparison badges (active comparison first, both shown) ----
    revenueBadges() {
      return this.buildHeroBadges(this.c && this.c.summary && this.c.summary.revenue);
    },

    // ---- Clinic-wide utilisation = average of branch utilPct (ignoring nulls) ----
    // The clinic summary carries no util field, so derive it like the branch view's
    // clinicAvg does. null when no branch reports utilisation.
    clinicUtilPct() {
      if (!this.c || !this.c.branches) return null;
      const vals = this.c.branches
        .map((b) => b.utilPct)
        .filter((v) => v != null && Number.isFinite(v));
      if (!vals.length) return null;
      return Math.round(vals.reduce((s, v) => s + v, 0) / vals.length);
    },

    // ---- Branches sorted by revenue ----
    sortedBranches() {
      if (!this.c || !this.c.branches) return [];
      return [...this.c.branches].sort(
        (a, b) => ((b.revenue && b.revenue.value) || 0) - ((a.revenue && a.revenue.value) || 0)
      );
    },

    // ---- Daily (per-bucket) revenue target, pro-rated to the viewed range ----
    // Used for the dashed reference line on the trend chart. null when no target.
    // Same caveat as the hero card: fullTarget only sums branches that have a target
    // set, while the plotted revenue covers every branch in scope — the label reuses
    // revenueTargetNote (template, above) so the line carries the same "จาก x/y สาขา"
    // qualifier instead of silently under-representing the bar.
    dailyTarget() {
      if (!this.c || !this.c.summary || !this.c.trend || !this.c.trend.length) return null;
      const prog = kpiTargetProgress({
        revenueTarget: this.c.summary.revenueTarget,
        actual: this.c.summary.revenue.value,
        startDt: this.period && this.period.startDt,
        endDt: this.period && this.period.endDt,
      });
      if (!prog || !prog.displayedTarget) return null;
      return prog.displayedTarget / this.c.trend.length;
    },

    // ---- Trend chart (Chart.js line+area; dashed daily-target reference line) ----
    // แท่งซ้อน: ค่ารักษาที่เก็บเงินได้ + มัดจำที่รับใหม่ = เงินเข้าจริง (ความสูงรวมของแท่ง)
    // มัดจำเก่าที่ถูกใช้ไม่อยู่ในแท่ง — อ่านได้จาก tooltip และการ์ดรายได้ด้านบน
    trendChartData() {
      if (!this.c || !this.c.trend || !this.c.trend.length) return null;
      const pts = this.c.trend;
      const labels = pts.map((p) => this.trendLabel(p.date));
      const datasets = [
        {
          label: "ค่ารักษา",
          data: pts.map((p) => p.treatmentCash || 0),
          backgroundColor: "#2196F3",
          stack: "rev",
          yAxisID: "y-rev",
        },
      ];
      if (this.hasDepositActivity) {
        datasets.push({
          label: "รับมัดจำ",
          data: pts.map((p) => p.depositReceived || 0),
          backgroundColor: "#26C6DA",
          stack: "rev",
          yAxisID: "y-rev",
        });
      }
      // Appointments line
      datasets.push({
        type: "line",
        label: "นัด",
        data: pts.map(p => p.appointments || 0),
        backgroundColor: "transparent",
        borderColor: "#F59E0B",
        borderWidth: 1.8,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
        lineTension: 0.35,
        yAxisID: "y-appt",
      });
      // Dashed reference line at the per-bucket revenue target (not for hourly view).
      if (this.dailyTarget != null && !this.isHourlyTrend) {
        datasets.push({
          type: "line",
          label: "เป้าเฉลี่ย",
          data: pts.map(() => this.dailyTarget),
          backgroundColor: "transparent",
          borderColor: "#9AA6BA",
          borderWidth: 1.4,
          borderDash: [5, 4],
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          yAxisID: "y-rev",
        });
      }
      return { labels, datasets };
    },
    trendChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        elements: { line: { capBezierPoints: false } },
        tooltips: {
          mode: "index",
          intersect: false,
          // ซ่อนเส้นเป้าที่แบนราบ — อิงชื่อ dataset ไม่ใช่ index เพราะจำนวน dataset
          // เปลี่ยนตามว่าช่วงนั้นมีมัดจำหรือไม่
          filter: (item, data) => data.datasets[item.datasetIndex].label !== "เป้าเฉลี่ย",
          callbacks: {
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex];
              if (ds.label === "นัด") return ds.label + ": " + tooltipItem.yLabel + " นัด";
              return ds.label + ": ฿" + formatNumber(tooltipItem.yLabel || 0);
            },
            // ยอดรวมของแท่ง = เงินเข้าจริงของ bucket นั้น (ไม่รวมเส้นนัด/เส้นเป้า)
            footer: (items, data) => {
              const total = items
                .filter((i) => ["ค่ารักษา", "รับมัดจำ"].includes(data.datasets[i.datasetIndex].label))
                .reduce((sum, i) => sum + Number(i.yLabel || 0), 0);
              return "เงินเข้าจริง: ฿" + formatNumber(total);
            },
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              gridLines: { display: false },
              ticks: { fontColor: "#6B7280", fontSize: 9, maxTicksLimit: 12, autoSkip: true },
            },
          ],
          yAxes: [
            {
              id: "y-rev",
              stacked: true,
              gridLines: { color: "#E5EAF0" },
              ticks: {
                fontColor: "#6B7280",
                fontSize: 10,
                beginAtZero: true,
                callback: (v) => {
                  if (v >= 1000000) return (v / 1000000).toFixed(1) + "M";
                  if (v >= 1000) return (v / 1000).toFixed(0) + "K";
                  return v;
                },
              },
            },
            {
              id: "y-appt",
              position: "right",
              stacked: false,
              gridLines: { display: false },
              ticks: {
                fontColor: "#F59E0B",
                fontSize: 9,
                beginAtZero: true,
                callback: (v) => (Number.isInteger(v) ? v : ""),
              },
            },
          ],
        },
      };
    },
    trendSubLabel() {
      const scope = `รายได้รวม${this.scopeLabel}`;
      if (this.isHourlyTrend) return `${scope} · รายชั่วโมง`;
      if (this.isMonthlyTrend) return `${scope} · รายเดือน`;
      return `${scope} · รายวัน`;
    },
    isMonthlyTrend() {
      // Monthly trend dates are "YYYY-MM" (length 7); daily are "YYYY-MM-DD" (length 10)
      return !!(
        this.c &&
        this.c.trend &&
        this.c.trend.length &&
        this.c.trend[0].date &&
        this.c.trend[0].date.length === 7
      );
    },
    isHourlyTrend() {
      return !!(this.c && this.c.bucket === 'hour');
    },

    // ---- Case mix (donut) ----
    // Build a stable category→color map keyed by name so the same category gets
    // the same color in both the current and previous period donuts even when
    // their revenue-DESC sort orders differ.
    // Base order: current period (so current donut keeps its revenue-ranked palette).
    // Categories that only appear in the previous period continue the index past
    // the current set so they still get a distinct, stable color.
    caseMixColorByCategory() {
      const map = {};
      const current = (this.c && this.c.caseMix) || [];
      let idx = 0;
      for (const item of current) {
        if (!(item.category in map)) {
          map[item.category] = CASE_MIX_COLORS[idx % CASE_MIX_COLORS.length];
          idx++;
        }
      }
      const prev = (this.c && this.c.prevPeriodCaseMix) || [];
      for (const item of prev) {
        if (!(item.category in map)) {
          map[item.category] = CASE_MIX_COLORS[idx % CASE_MIX_COLORS.length];
          idx++;
        }
      }
      return map;
    },
    caseMixItems() {
      if (!this.c || !this.c.caseMix || !this.c.caseMix.length) return null;
      const colorMap = this.caseMixColorByCategory;
      return this.c.caseMix.map((item) => ({
        ...item,
        color: colorMap[item.category],
      }));
    },
    prevCaseMixItems() {
      if (!this.c || !this.c.prevPeriodCaseMix || !this.c.prevPeriodCaseMix.length) return null;
      const colorMap = this.caseMixColorByCategory;
      return this.c.prevPeriodCaseMix.map((item) => ({
        ...item,
        color: colorMap[item.category],
      }));
    },
    caseMixChartData() {
      const items = this.caseMixItems || [];
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => this.caseMixMode === 'count' ? (c.count || 0) : (c.pct || 0)),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixChartDataPrev() {
      const items = this.prevCaseMixItems || [];
      return {
        labels: items.map((c) => c.category),
        datasets: [
          {
            data: items.map((c) => this.caseMixMode === 'count' ? (c.count || 0) : (c.pct || 0)),
            backgroundColor: items.map((c) => c.color),
            borderWidth: 2,
            borderColor: "#fff",
          },
        ],
      };
    },
    caseMixOptions() {
      return {
        responsive: true,
        // Keep the donut square and sized by width only. With maintainAspectRatio
        // false the canvas tracks its (flex-fill) parent's height and balloons
        // past the 150px box, drifting below the legend.
        maintainAspectRatio: true,
        aspectRatio: 1,
        cutoutPercentage: 58,
        legend: { display: false },
        tooltips: {
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.labels[tooltipItem.index] || "";
              const val = data.datasets[0].data[tooltipItem.index];
              return this.caseMixMode === 'count'
                ? label + ": " + val + " เคส"
                : label + ": " + val + "%";
            },
          },
        },
      };
    },

    // ---- Doctor section ----
    sortedDoctors() {
      return [...((this.c && this.c.topDoctors) || [])].sort((a, b) => (b.revenue || 0) - (a.revenue || 0));
    },
    maxDoctorRevenue() {
      if (!this.sortedDoctors.length) return 1;
      return this.sortedDoctors[0].revenue || 1;
    },
    totalDoctorRevenue() {
      return this.sortedDoctors.reduce((s, doc) => s + (doc.revenue || 0), 0);
    },

    // ---- Top treatments bar max ----
    maxTreatmentRevenue() {
      if (!this.c || !this.c.topTreatments || !this.c.topTreatments.length) return 1;
      return Math.max(...this.c.topTreatments.map((t) => t.revenue || 0)) || 1;
    },

    // ---- Referral bar max ----
    maxReferralCount() {
      if (!this.c || !this.c.referralSources || !this.c.referralSources.length) return 1;
      return Math.max(...this.c.referralSources.map((s) => s.count || 0)) || 1;
    },
  },

  methods: {
    stripTitle(name) {
      if (!name) return "";
      return name.replace(/^[^.]+\.\s*/, "").trim();
    },
    // Active comparison first, both badges shown (mirrors BranchDashboard hero).
    buildHeroBadges(metric) {
      if (!metric) return [];
      const comparison = this.period && this.period.comparison;
      const order =
        comparison === "prevPeriod"
          ? ["prevPeriod", "prevYear"]
          : ["prevYear", "prevPeriod"];
      const tags = { prevPeriod: this.prevPeriodTag, prevYear: this.prevYearTag };
      return order
        .filter((k) => Number.isFinite(metric[k + "Pct"]))
        .map((k, i) => ({ pct: metric[k + "Pct"], tag: tags[k], active: i === 0 }));
    },

    onPeriodChange(p) {
      this.period = p;
      this.load();
    },

    async load() {
      if (!this.period) return;
      this.error = null;
      this.loading = true;
      try {
        this.c = await this.$store.dispatch("dashboard/fetchClinicDashboard", {
          clinicUrl: this.clinicUrl,
          startDt: this.period.startDt,
          endDt: this.period.endDt,
        });
      } catch (e) {
        this.error = e;
        this.$bvToast.toast("โหลดข้อมูลแดชบอร์ดไม่สำเร็จ", {
          variant: "danger",
          title: "ข้อผิดพลาด",
          autoHideDelay: 4000,
        });
      } finally {
        this.loading = false;
      }
    },

    openDrawer(branchUrl, branchName) {
      this.activeBranchUrl = branchUrl;
      this.activeBranchName = branchName || "";
      this.drawerOpen = true;
    },

    openFull(branchUrl) {
      // Carry the selected period (type + exact month/year/day) so the branch
      // view opens on the same period instead of resetting to the current one.
      const p = this.period || {};
      const query = { viewType: p.viewType };
      if (p.startDt) {
        const s = moment(p.startDt);
        if (p.viewType === "year") query.year = s.format("YYYY");
        else if (p.viewType === "day") query.day = s.format("YYYY-MM-DD");
        else query.month = s.format("YYYY-MM");
      }
      this.$router.push({
        name: "CentralBranchDashboard",
        params: { clinicUrl: this.clinicUrl, branchUrl, branchName: this.activeBranchName },
        query,
      });
    },

    // ---- Alert helpers ----
    // Alerts: { type:"REVENUE_DROP"|"NO_SHOW"|"LOW_UTIL", severity:"red"|"orange", branchUrl, value }
    alertLabel(alert) {
      const name = this.branchNameByUrl[alert.branchUrl];
      const prefix = name ? name + " " : "";
      switch (alert.type) {
        case "REVENUE_DROP":
          return prefix + "รายได้ −" + Math.abs(alert.value || 0).toFixed(1) + "% " + this.comparisonTag;
        case "NO_SHOW":
          return prefix + "No-show " + (alert.value || 0).toFixed(1) + "%";
        case "LOW_UTIL":
          return prefix + "ใช้ยูนิตเฉลี่ย " + (alert.value || 0) + "%";
        default:
          return name || "";
      }
    },
    alertIcon(alert) {
      return alert.type === "REVENUE_DROP" ? "▼" : "⚠";
    },
    alertChipClass(alert) {
      return alert.severity === "red" ? "alert-chip--red" : "alert-chip--orange";
    },

    // ---- Branch row helpers ----
    // แถวสาขาจะโชว์บรรทัดมัดจำเฉพาะสาขาที่มีความเคลื่อนไหวจริง
    branchHasDeposit(branch) {
      const r = branch && branch.revenue;
      return !!r && !!(r.depositReceived || r.depositUsed);
    },
    branchRevenuePct(branch) {
      if (!branch.revenue) return null;
      const comparison = this.period && this.period.comparison;
      const v = branch.revenue[(comparison || "prevPeriod") + "Pct"];
      return v == null ? null : v;
    },
    noShowPillClass(pct) {
      const v = pct || 0;
      return v >= 5 ? "pill--bad" : v >= 4 ? "pill--warn" : "pill--ok";
    },
    utilPillClass(pct) {
      const v = pct || 0;
      return v < 50 ? "pill--bad" : v < 60 ? "pill--warn" : "pill--ok";
    },
    // ---- Doctor bar widths / shares ----
    doctorShare(revenue) {
      if (!this.totalDoctorRevenue) return 0;
      return ((revenue || 0) / this.totalDoctorRevenue * 100).toFixed(1);
    },
    doctorBarWidth(revenue) {
      if (!this.maxDoctorRevenue) return 0;
      return Math.round(((revenue || 0) / this.maxDoctorRevenue) * 100);
    },

    // ---- Bar widths ----
    treatBarWidth(revenue) {
      return Math.round(((revenue || 0) / this.maxTreatmentRevenue) * 100);
    },
    referralBarWidth(count) {
      return Math.round(((count || 0) / this.maxReferralCount) * 100);
    },

    // ---- Trend label mapping (daily YYYY-MM-DD -> day number; monthly YYYY-MM -> month label; hourly YYYY-MM-DD HH -> HH:00) ----
    trendLabel(date) {
      if (!date) return "";
      if (date.length === 7) return moment(date, "YYYY-MM").locale("th").format("MMM");
      if (date.length === 10) return moment(date, "YYYY-MM-DD").locale("th").format("D MMM");
      // hourly: "YYYY-MM-DD HH" (length 13)
      return moment(date, "YYYY-MM-DD HH").format("HH:00");
    },

    // ---- Formatting ----
    formatNumber(val) {
      return formatNumber(val || 0);
    },
    formatShort(val) {
      if (val == null) return "–";
      if (Math.abs(val) >= 1000000) return (val / 1000000).toFixed(2) + "M";
      if (Math.abs(val) >= 1000) return (val / 1000).toFixed(0) + "K";
      return formatNumber(val);
    },
    formatRange,
  },

  watch: {
    // Vue Router reuses this instance when only :clinicUrl changes — refetch for new clinic.
    clinicUrl() {
      this.c = null;
      this.load();
    },
  },
};
</script>

<style scoped>
/* ---- Brand tokens ---- */
.clinic-dashboard {
  --cd-primary: #2196f3;
  --cd-primary-dark: #1976d2;
  --cd-primary-light: #e3f2fd;
  --cd-green: #22c55e;
  --cd-red: #ef4444;
  --cd-orange: #f59e0b;
  --cd-purple: #8b5cf6;
  --cd-bg: #f5f8fb;
  --cd-border: #e5eaf0;
  --cd-text-muted: #6b7280;
  background: var(--cd-bg);
  min-height: calc(100vh - 56px);
}

/* ---- Utilities ---- */
.text-green        { color: var(--cd-green)  !important; }
.text-red          { color: var(--cd-red)    !important; }
.text-orange       { color: var(--cd-orange) !important; }
.text-primary-dark { color: var(--cd-primary-dark) !important; }
.label-muted       { color: var(--cd-text-muted); }
.font-weight-semibold { font-weight: 600; }

/* ---- Range picker (segmented control) ---- */
.cd-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid #d2dae6;
  border-radius: 9px;
  padding: 3px;
}
.cd-seg--ghost {
  border-color: var(--cd-border);
}
.cd-seg-btn {
  font-size: 12px;
  font-weight: 600;
  color: var(--cd-text-muted);
  background: none;
  border: 0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;
}
.cd-seg-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.cd-seg-btn.on {
  background: var(--cd-primary);
  color: #fff;
}
.cd-seg--ghost .cd-seg-btn.on {
  background: var(--cd-primary-light);
  color: var(--cd-primary-dark);
}
/* Period dropdown — match the segmented pickers / target button (36px, 9px radius). */
.cd-period-select.custom-select {
  width: auto;
  min-width: 150px;
  height: 36px;
  border-radius: 9px;
  border-color: #d2dae6;
  font-size: 12px;
  font-weight: 600;
}

/* "ตั้งเป้าหมาย" trigger — match the segmented pickers: 36px tall, 9px radius,
   12px/600 text, brand blue (not Bootstrap's default primary). */
.btn.cd-target-btn {
  height: 36px;
  display: inline-flex;
  align-items: center;
  padding: 0 14px;
  border-radius: 9px;
  font-size: 12px;
  font-weight: 600;
  background: var(--cd-primary);
  border-color: var(--cd-primary);
}
.btn.cd-target-btn:hover,
.btn.cd-target-btn:focus,
.btn.cd-target-btn:active {
  background: var(--cd-primary-dark);
  border-color: var(--cd-primary-dark);
}

/* ---- Alert chips ---- */
.alert-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
}
.alert-chip:hover { opacity: 0.8; }
.alert-chip--red { background: #ef444412; color: var(--cd-red); }
.alert-chip--orange { background: #f59e0b15; color: var(--cd-orange); }

/* ---- KPI cards ---- */
.kpi-card {
  border: 1px solid var(--cd-border);
  border-radius: 12px;
}
.kpi-num {
  font-size: clamp(20px, 2.2vw, 30px);
  font-weight: 700;
  line-height: 1;
}

/* ---- KPI band grid: featured hero (1.75fr) + 4 tiles ---- */
.cd-kpi-grid {
  display: grid;
  grid-template-columns: 1.75fr 1fr 1fr 1fr 1fr;
  gap: 12px;
}
.cd-kpi-grid > .card { margin-bottom: 0; }
@media (max-width: 1199px) {
  .cd-kpi-grid { grid-template-columns: 1fr 1fr; }
  .cd-kpi-grid > .cd-kpi-hero { grid-column: 1 / -1; }
}
@media (max-width: 575px) {
  .cd-kpi-grid { grid-template-columns: 1fr; }
}

/* ---- Featured revenue hero (mirrors BranchDashboard .hero-card--feat) ---- */
.cd-kpi-hero {
  border: 0;
  background: linear-gradient(135deg, #1d3a73, #2a6df4);
  color: #fff;
}
.cd-kpi-hero .unit-baht,
.cd-kpi-hero .label-feat { color: #fff; }
.label-feat { color: #dbe6ff; opacity: 0.9; }
.hero-num-lg {
  font-size: clamp(28px, 3.2vw, 42px);
  font-weight: 700;
  line-height: 1;
}
.unit-baht { font-size: 1.1rem; font-weight: 600; opacity: 0.85; }

/* Hero comparison badges */
.badges { display: flex; gap: 8px; flex-wrap: wrap; }
.hero-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #eaf1ff;
}
.hero-badge.active { box-shadow: 0 1px 0 #fff inset; }
.hero-badge-v.up { color: #7ff0c4; }
.hero-badge-v.down { color: #ffb3a5; }
.hero-badge-tag { font-size: 10px; font-weight: 600; opacity: 0.7; }

/* Dashed legend swatch for the trend daily-target reference line */
.cd-target-dash {
  display: inline-block;
  width: 16px;
  border-top: 1.6px dashed #9aa6ba;
}

/* ---- Legend dot ---- */
.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* ---- Top treatments ---- */
.treat-row {
  display: grid;
  grid-template-columns: 22px 1fr;
  align-items: center;
  gap: 12px;
}
.treat-rank {
  font-weight: 700;
  color: var(--cd-text-muted);
  font-size: 13px;
  text-align: center;
}
.treat-track {
  height: 7px;
  border-radius: 6px;
  background: var(--cd-bg);
  overflow: hidden;
}
.treat-fill {
  height: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}

/* ---- Referral bar ---- */
.referral-name {
  flex: 0 0 96px;
  color: var(--cd-text-muted);
}
.referral-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--cd-bg);
  height: 7px;
  overflow: hidden;
}
.referral-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #0f9d72, #3ec79a);
  transition: width 0.4s ease;
}

/* ---- Branches table ---- */
.cd-branch-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.cd-branch-table thead th {
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--cd-text-muted);
  padding: 10px 16px;
  border-top: 1px solid var(--cd-border);
  border-bottom: 1px solid var(--cd-border);
  background: var(--cd-bg);
}
.cd-branch-table thead th.text-left { text-align: left; }
.cd-branch-table tbody td {
  padding: 13px 16px;
  text-align: right;
  border-bottom: 1px solid var(--cd-border);
  font-variant-numeric: tabular-nums;
}
.cd-branch-table tbody td.text-left { text-align: left; }
.cd-branch-table tbody tr:last-child td { border-bottom: 0; }
.cd-branch-row { cursor: pointer; transition: background 0.12s; }
.cd-branch-row:hover { background: var(--cd-bg); }
.bname { font-weight: 600; color: #1f2a37; }
.bsub { font-size: 11.5px; color: var(--cd-text-muted); font-weight: 400; }
.bdeposit {
  font-size: 11px;
  color: var(--cd-text-muted);
  font-weight: 400;
  margin-top: 2px;
  white-space: nowrap;
}

/* ---- Hero revenue breakdown ---- */
.hero-breakdown {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: 12.5px;
}
.hero-breakdown > div {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  color: #dbe6ff;
}
.hero-breakdown b { color: #fff; }
.money { font-weight: 700; }
.branch-delta {
  font-size: 11.5px;
  font-weight: 600;
  display: inline-block;
  margin-left: 6px;
}
.pill {
  display: inline-block;
  font-weight: 600;
  font-size: 12px;
  padding: 3px 9px;
  border-radius: 7px;
}
.pill--ok { background: #e6f6f0; color: #0f9d72; }
.pill--warn { background: #fdf3e2; color: var(--cd-orange); }
.pill--bad { background: #fdece8; color: var(--cd-red); }

/* ---- Doctor bar (clinic dashboard) ---- */
.cd-doctor-bar-track {
  position: relative;
  border-radius: 999px;
  background: var(--cd-bg);
  height: 8px;
  overflow: hidden;
}
.cd-doctor-bar-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #2196f3, #5b8dff);
  transition: width 0.4s ease;
}
.cd-doctor-bars {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.cd-doctor-bar-fill--df {
  background: linear-gradient(90deg, #26c6da, #5be0d0);
}

/* ---- Case mix dual columns ---- */
.cd-casemix-col {
  min-width: 120px;
}
.cd-casemix-empty {
  text-align: center;
  padding-top: 6px;
}
.cd-casemix-empty-ring {
  width: 130px;
  height: 130px;
  margin: 0 auto;
  border-radius: 50%;
  border: 14px solid var(--cd-bg, #eef1f6);
}
@media (max-width: 575px) {
  .cd-casemix-col { flex: 0 0 100%; }
}
</style>
